import { useState } from "react";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Done from "@mui/icons-material/Done";
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ok } from "neverthrow";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { match } from "ts-pattern";

import { mustBeDefined, mustParse } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  FormSection,
  FormSectionCompletedEndIncident,
  FormSectionCompleteFinishForm,
  FormSectionCompleteMcaAccompaniment,
  FormSectionCompletePos,
  FormSectionCompleteProps,
  FormSectionCompleteTriage,
  IncidentType,
  IncidentWorkItemId,
  TranslationKey,
} from "@thalamos/vision-common";
import {
  buildIncidentFormPdfFilename,
  FormSectionSchema,
  IncidentTypeSchema,
  IncidentWorkItemIdSchema,
  tk,
} from "@thalamos/vision-common";

import api from "../../api/index.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { FormWrapper } from "../../layout/FormWrapper.js";
import { createRoute } from "../../routing/createRoute.js";
import { TypographyI18N } from "../TypographyI18N/TypographyI18N.js";

/**
 * Determines if the officer is accompanying the person to the Emergency Department.
 * Used by both the Body (label) and Footer (routing) components to ensure consistent behaviour.
 */
const isAccompanyingToEmergencyDepartment = (
  payload: FormSectionCompleteProps["payload"],
): boolean => {
  const mcaPayload = payload as FormSectionCompleteMcaAccompaniment | undefined;
  return mcaPayload?.accompanyingToEmergencyDepartment === "yes";
};

/**
 * Not every completion payload carries an incident type; `getIncidentFormPdfName`
 * decides what an absent one is called.
 */
const endedIncidentType = (
  payload: FormSectionCompleteProps["payload"],
): IncidentType | undefined =>
  payload !== undefined && "incidentType" in payload
    ? payload.incidentType
    : undefined;

const PlaceOfSafetyBody = ({ pos, arrivalDate }: FormSectionCompletePos) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          paddingBottom: "1rem",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={tk("formSuccess.confirmed")}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
      <Box>
        <TypographyI18N translationKey={tk("formSuccess.currentPos")} />
        <Typography
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {pos}
        </Typography>
        <Typography
          sx={{
            paddingTop: "1rem",
            color: "#2e7d32",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          {arrivalDate}
        </Typography>
      </Box>
      <Box>
        <TypographyI18N translationKey={tk("formSuccess.proceedToShare")} />
      </Box>
    </Stack>
  );
};

const FormSectionCompleteBody = ({
  body,
  proceedTo,
}: {
  body: TranslationKey;
  proceedTo: TranslationKey;
}) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={tk("formSuccess.formComplete")}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
      <Box>
        <TypographyI18N translationKey={body} />
      </Box>
      <Box>
        <TypographyI18N translationKey={tk("formSuccess.proceedWith")} />
        <TypographyI18N
          translationKey={proceedTo}
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        />
      </Box>
    </Stack>
  );
};

const TriageIncidentBody = ({
  incidentType,
}: {
  incidentType: IncidentType | undefined;
}) => {
  const theme = useTheme();

  const formNameTranslationKey = match(incidentType)
    .with(IncidentTypeSchema.enum.section136, () =>
      tk("formSuccess.section136Form"),
    )
    .with(IncidentTypeSchema.enum.mentalCapacityAct, () =>
      tk("formSuccess.mcaForm"),
    )
    .with(IncidentTypeSchema.enum.voluntary, () =>
      tk("formSuccess.voluntaryForm"),
    )
    .with(IncidentTypeSchema.enum.other, () => tk("formSuccess.otherForm"))
    .with(undefined, () => tk("formSuccess.section136Form"))
    .exhaustive();

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={tk("formSuccess.formComplete")}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
      <Box>
        <TypographyI18N translationKey={tk("formSuccess.triageComplete")} />
      </Box>
      <Box>
        <TypographyI18N translationKey={tk("formSuccess.proceedWith")} />
        <TypographyI18N
          translationKey={formNameTranslationKey}
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        />
      </Box>
    </Stack>
  );
};

const FinishFormBody = ({
  incidentType,
}: {
  incidentType: IncidentType | undefined;
}) => {
  const theme = useTheme();

  const hasBeenEndedTranslationKey = match(incidentType)
    .with(IncidentTypeSchema.enum.section136, () =>
      tk("formSuccess.formEnded136"),
    )
    .with(IncidentTypeSchema.enum.mentalCapacityAct, () =>
      tk("formSuccess.formEndedMca"),
    )
    .with(IncidentTypeSchema.enum.voluntary, () =>
      tk("formSuccess.formEndedVoluntary"),
    )
    .with(IncidentTypeSchema.enum.other, () => tk("formSuccess.formEndedOther"))
    .with(undefined, () => tk("formSuccess.formEnded136"))
    .exhaustive();

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={tk("formSuccess.formComplete")}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
      <Box>
        <TypographyI18N
          translationKey={hasBeenEndedTranslationKey}
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        />
      </Box>
      <Box>
        <TypographyI18N
          translationKey={tk("formSuccess.formEndedArchived")}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
    </Stack>
  );
};

const PreEndIncidentBody = ({
  endingReason,
  incidentType,
}: {
  endingReason?: string;
  incidentType?: IncidentType;
}) => {
  const theme = useTheme();

  const formEndedTranslationKey = match(incidentType)
    .with(IncidentTypeSchema.enum.section136, () =>
      tk("formSuccess.formEnded136"),
    )
    .with(IncidentTypeSchema.enum.mentalCapacityAct, () =>
      tk("formSuccess.formEndedMca"),
    )
    .with(IncidentTypeSchema.enum.voluntary, () =>
      tk("formSuccess.formEndedVoluntary"),
    )
    .with(IncidentTypeSchema.enum.other, () => tk("formSuccess.formEndedOther"))
    .with(undefined, () => tk("formSuccess.formEnded136"))
    .exhaustive();

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={formEndedTranslationKey}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
      {endingReason && (
        <Box>
          <Typography
            sx={{
              paddingTop: "1rem",
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            {endingReason}
          </Typography>
        </Box>
      )}

      <Box>
        <TypographyI18N
          translationKey={tk("formSuccess.formEndedArchived")}
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
    </Stack>
  );
};

export const PolicePresenceRecorded = () => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        margin: "2rem 0",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
        }}
      >
        <CheckCircle color="success" />
        <TypographyI18N
          translationKey={tk("formSuccess.policePresenceComplete")}
          sx={{
            marginLeft: "1rem",
            fontWeight: theme.typography.fontWeightMedium,
          }}
        />
      </Box>
    </Stack>
  );
};

const Body = ({ type, payload }: FormSectionCompleteProps) => {
  const { t } = useTranslation();
  switch (type) {
    case FormSectionSchema.enum.createIncidentMain:
      return (
        <FormSectionCompleteBody
          // TODO: How do we add the officer name whilst remaining translatable?
          // body={`${t("formSuccess.mentalHealthForm")} ${(payload as FormSectionCompleteIncidentMain).officer}`}
          body={tk("formSuccess.mentalHealthForm")}
          proceedTo={tk("formSuccess.incidentTriage")}
        />
      );
    case FormSectionSchema.enum.triageMain:
      return (
        <TriageIncidentBody
          incidentType={
            (payload as FormSectionCompleteTriage | undefined)?.incidentType
          }
        />
      );
    case FormSectionSchema.enum.personDetails:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.personDetailsComplete")}
          proceedTo={tk("formSuccess.detentionDetails")}
        />
      );
    case FormSectionSchema.enum.detentionDetails:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.detentionDetailsComplete")}
          proceedTo={tk("formSuccess.informationForHealthcareStaff")}
        />
      );
    case FormSectionSchema.enum.informationForHealthcare:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.informationForHealthcareComplete")}
          proceedTo={tk("formSuccess.addPOS")}
        />
      );
    case FormSectionSchema.enum.mcaPersonDetails:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.mcaPersonDetailsComplete")}
          proceedTo={tk("formSuccess.mcaIncidentDetails")}
        />
      );
    case FormSectionSchema.enum.mcaIncidentDetails:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.mcaIncidentDetailsComplete")}
          proceedTo={tk("formSuccess.mcaCapacityAssessment")}
        />
      );
    case FormSectionSchema.enum.mcaCapacityAssessment:
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.mcaCapacityAssessmentComplete")}
          proceedTo={tk("formSuccess.mcaAccompaniment")}
        />
      );
    case FormSectionSchema.enum.mcaAccompaniment: {
      return (
        <FormSectionCompleteBody
          body={tk("formSuccess.mcaAccompanimentComplete")}
          proceedTo={
            isAccompanyingToEmergencyDepartment(payload)
              ? tk("formSuccess.addPOS_mca")
              : tk("formSuccess.endForm")
          }
        />
      );
    }
    case FormSectionSchema.enum.recordPlaceOfSafetyMain:
      return (
        <PlaceOfSafetyBody
          pos={(payload as FormSectionCompletePos).pos}
          arrivalDate={`${t("formSuccess.arrivedAt")} ${(payload as FormSectionCompletePos).arrivalDate}`}
        />
      );
    case FormSectionSchema.enum.preEndIncident:
      return (
        <PreEndIncidentBody
          endingReason={
            (payload as FormSectionCompletedEndIncident).endingReason
              ?.valueLabel
              ? `${t("formSuccess.formEndedReason")} ${(payload as FormSectionCompletedEndIncident).endingReason.valueLabel}`
              : undefined
          }
          incidentType={
            (payload as FormSectionCompletedEndIncident | undefined)
              ?.incidentType
          }
        />
      );
    case FormSectionSchema.enum.recordPolicePresence:
      return <PolicePresenceRecorded />;
    case FormSectionSchema.enum.finishForm:
      return (
        <FinishFormBody
          incidentType={
            (payload as FormSectionCompleteFinishForm | undefined)?.incidentType
          }
        />
      );
    default:
      return <></>;
  }
};

const Header = ({ type }: { type: FormSection }) => {
  const theme = useTheme();

  const getTitle = (type: FormSection): TranslationKey => {
    return match(type)
      .with(FormSectionSchema.enum.createIncidentMain, () =>
        tk("formSuccess.mentalHealthFormTitle"),
      )
      .with(FormSectionSchema.enum.triageMain, () =>
        tk("formSuccess.incidentTriage"),
      )
      .with(FormSectionSchema.enum.personDetails, () =>
        tk("formSuccess.personDetails"),
      )
      .with(FormSectionSchema.enum.detentionDetails, () =>
        tk("formSuccess.detentionDetails"),
      )
      .with(FormSectionSchema.enum.informationForHealthcare, () =>
        tk("formSuccess.informationForHealthcare"),
      )
      .with(FormSectionSchema.enum.recordPlaceOfSafetyMain, () =>
        tk("formSuccess.addPOS"),
      )
      .with(FormSectionSchema.enum.recordPlaceOfSafetyArrival, () =>
        tk("formSuccess.arrivePOS"),
      )
      .with(FormSectionSchema.enum.recordPlaceOfSafetyLeave, () =>
        tk("formSuccess.leavePOS"),
      )
      .with(FormSectionSchema.enum.preEndIncident, () =>
        tk("formSuccess.endForm"),
      )
      .with(FormSectionSchema.enum.mcaPersonDetails, () =>
        tk("formSuccess.mcaPersonDetails"),
      )
      .with(FormSectionSchema.enum.mcaIncidentDetails, () =>
        tk("formSuccess.mcaIncidentDetails"),
      )
      .with(FormSectionSchema.enum.mcaCapacityAssessment, () =>
        tk("formSuccess.mcaCapacityAssessment"),
      )
      .with(FormSectionSchema.enum.mcaAccompaniment, () =>
        tk("formSuccess.mcaAccompaniment"),
      )
      .with(
        FormSectionSchema.enum.s136FormRejected,
        FormSectionSchema.enum.shareIncident,
        FormSectionSchema.enum.requestPolicePresenceMain,
        FormSectionSchema.enum.recordPolicePresence,
        FormSectionSchema.enum.finishForm,
        () => tk("formSuccess.mentalHealthFormTitle"),
      )
      .exhaustive();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          paddingBottom: "1rem",
          justifyContent: "space-between",
        }}
      >
        <TypographyI18N
          translationKey={getTitle(type)}
          sx={{ fontWeight: theme.typography.fontWeightBold }}
        />
        <Done color="success" />
      </Box>
      <Divider
        sx={{
          borderColor: theme.palette.primary.main,
          borderWidth: "2px",
        }}
      />
    </Box>
  );
};

const Footer = ({
  type,
  payload,
}: {
  type: FormSection;
  payload?: FormSectionCompleteProps["payload"];
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const notifications = useToastNotifications();
  const [isDownloading, setIsDownloading] = useState(false);

  const incidentWorkItemId = ok(id)
    .andThen(mustBeDefined())
    .andThen(mustParse(IncidentWorkItemIdSchema))
    .unwrapOr(undefined);

  const incidentEndedStates: Array<FormSection> = [
    FormSectionSchema.enum.preEndIncident,
    FormSectionSchema.enum.finishForm,
  ];
  const incidentEndedState = incidentEndedStates.includes(type);

  const getProceedLocation = (
    type: FormSection,
    incidentId: IncidentWorkItemId,
  ) => {
    return match(type)
      .with(FormSectionSchema.enum.createIncidentMain, () =>
        createRoute.incidentTriagePage(incidentId),
      )
      .with(FormSectionSchema.enum.triageMain, () =>
        createRoute.incidentProgressPage(incidentId),
      )
      .with(FormSectionSchema.enum.personDetails, () =>
        createRoute.incidentDetentionDetailsPage(incidentId),
      )
      .with(FormSectionSchema.enum.detentionDetails, () =>
        createRoute.incidentInformationForHealthcarePage(incidentId),
      )
      .with(FormSectionSchema.enum.informationForHealthcare, () =>
        createRoute.incidentProgressPage(incidentId),
      )
      .with(FormSectionSchema.enum.mcaPersonDetails, () =>
        createRoute.incidentMcaIncidentDetailsPage(incidentId),
      )
      .with(FormSectionSchema.enum.mcaIncidentDetails, () =>
        createRoute.incidentMcaCapacityAssessmentPage(incidentId),
      )
      .with(FormSectionSchema.enum.mcaCapacityAssessment, () =>
        createRoute.incidentMcaAccompanimentPage(incidentId),
      )
      .with(FormSectionSchema.enum.mcaAccompaniment, () =>
        isAccompanyingToEmergencyDepartment(payload)
          ? createRoute.incidentReviewPlacesOfSafetyPage(incidentId)
          : createRoute.incidentPreEndIncidentPage(incidentId),
      )
      .with(FormSectionSchema.enum.recordPolicePresence, () =>
        createRoute.incidentProgressPage(incidentId),
      )
      .with(
        FormSectionSchema.enum.recordPlaceOfSafetyMain,
        FormSectionSchema.enum.recordPlaceOfSafetyArrival,
        FormSectionSchema.enum.recordPlaceOfSafetyLeave,
        FormSectionSchema.enum.preEndIncident,
        FormSectionSchema.enum.requestPolicePresenceMain,
        FormSectionSchema.enum.finishForm,
        FormSectionSchema.enum.shareIncident,
        FormSectionSchema.enum.s136FormRejected,
        () => createRoute.home(),
      )
      .exhaustive();
  };

  const handleDownloadOrNavigate = async () => {
    if (incidentWorkItemId === undefined) {
      return;
    }

    if (incidentEndedState) {
      try {
        setIsDownloading(true);
        const result = await api.incidentWorkItem.getPdf(incidentWorkItemId);
        if (result.status === 200) {
          const dataUri = `data:application/pdf;base64,${result.data}`;
          triggerDownload(
            dataUri,
            buildIncidentFormPdfFilename({
              incidentType: endedIncidentType(payload),
              date: new Date(),
            }),
          );
        } else {
          notifications.show(t("formSuccess.failedToDownloadPdf"), {
            severity: "error",
          });
        }
      } catch (_error) {
        notifications.show(t("formSuccess.failedToDownloadPdf"), {
          severity: "error",
        });
      } finally {
        setIsDownloading(false);
      }
    } else {
      navigate(getProceedLocation(type, incidentWorkItemId));
    }
  };

  return (
    <Box
      sx={{
        position: "sticky",
        bottom: 0,
        left: 0,
        width: "100%",
        background: "white",
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          color="error"
          sx={{
            textDecoration: "underline",
          }}
          onClick={() => {
            if (incidentEndedState) {
              navigate(createRoute.home());
            } else if (incidentWorkItemId !== undefined) {
              navigate(createRoute.incidentProgressPage(incidentWorkItemId));
            }
          }}
        >
          {t("common.close")}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleDownloadOrNavigate();
          }}
          color="primary"
          data-testid="proceed-button"
          disabled={isDownloading}
        >
          {incidentEndedState
            ? t("formSuccess.downloadPdf")
            : t("formSuccess.proceed")}
        </Button>
      </Box>
    </Box>
  );
};

export const FormSectionComplete = ({
  type,
  payload,
}: FormSectionCompleteProps) => {
  return (
    <FormWrapper>
      <Header type={type} />
      <Body type={type} payload={payload} />
      <Footer type={type} payload={payload} />
    </FormWrapper>
  );
};
